/**
 * When to ask about notifications (2026-10-11).
 *
 * The ask used to come once, after sealing — so someone who only receives
 * letters was never asked, and an iPhone in a Safari tab (where web push does
 * not exist) was never asked either. Now it comes at the moments a letter is
 * worth hearing about, and a "not now" is not forever: up to MAX_ASKS times, at
 * least a week apart. The browser's own prompt still only follows a yes here.
 */

import { isIOS, isStandalone } from '../install/platform';
import { pushSupported } from './push';

/** Where the ask came from — copy and analytics both key off it. */
export type AskReason = 'sealed' | 'incoming' | 'email';

/**
 * What can be offered on this device: the notification ask itself, or (on an
 * iPhone in a browser tab) the steps to put toiyp on the Home Screen first.
 */
export type AskKind = 'notify' | 'ios-install';

const KEY = 'toiyp:push_invite';
const MAX_ASKS = 3;
const SPACING_MS = 7 * 24 * 60 * 60 * 1000;

interface Asked {
  count: number;
  lastAt: number;
}

function read(): Asked {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { count: 0, lastAt: 0 };
    // The one-time ask stored '1'. Count it, but let the spacing start now
    // rather than never: those members get the new, better-timed asks too.
    if (raw === '1') return { count: 1, lastAt: 0 };
    const parsed = JSON.parse(raw) as Partial<Asked>;
    return { count: Number(parsed.count) || 0, lastAt: Number(parsed.lastAt) || 0 };
  } catch {
    // Storage blocked: behave as already asked, so nobody is asked every visit.
    return { count: MAX_ASKS, lastAt: 0 };
  }
}

export function askKind(): AskKind | null {
  if (pushSupported() && Notification.permission === 'default') return 'notify';
  if (isIOS() && !isStandalone()) return 'ios-install';
  return null;
}

/**
 * Worth asking now? `spaced: false` skips the week between asks — for the
 * arrival email, where the member has just shown they would like to be told.
 */
export function mayAsk(opts: { spaced?: boolean; now?: number } = {}): AskKind | null {
  const kind = askKind();
  if (!kind) return null;
  const { count, lastAt } = read();
  if (count >= MAX_ASKS) return null;
  if (opts.spaced !== false && (opts.now ?? Date.now()) - lastAt < SPACING_MS) return null;
  return kind;
}

const FROM_EMAIL_KEY = 'toiyp:from_arrival_email';

/**
 * The arrival email's button lands on `?from=arrival-email`. Called once at
 * start-up: the marker moves off the URL into this tab's session, so it
 * survives signing in, and is taken once by the letter list.
 */
export function noteArrivalEmail(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('from') !== 'arrival-email') return;
  params.delete('from');
  const rest = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash,
  );
  try {
    sessionStorage.setItem(FROM_EMAIL_KEY, '1');
  } catch {
    // private mode: the ask simply waits for the next letter
  }
}

export function takeArrivalEmail(): boolean {
  try {
    if (!sessionStorage.getItem(FROM_EMAIL_KEY)) return false;
    sessionStorage.removeItem(FROM_EMAIL_KEY);
    return true;
  } catch {
    return false;
  }
}

export function markAsked(now = Date.now()): void {
  const { count } = read();
  try {
    localStorage.setItem(KEY, JSON.stringify({ count: count + 1, lastAt: now }));
  } catch {
    // nothing to remember with
  }
}
